import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, Dialog,
  DialogActions, DialogContent, DialogTitle, IconButton, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography, InputAdornment,
} from '@mui/material'
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { ApiError, type DeviceWebhookEndpointIssued } from '@cmms/api-client'
import type { DeviceWebhookEvent } from '@cmms/types'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useConfirm } from '../../components/ConfirmDialog'
import { useOrgSettings } from '../../hooks/useOrgSettings'

// ═══════════════════════════════════════════════════════════════════════════
// PANEL A — Webhook endpoints (create a URL, hand it to the device platform)
// ═══════════════════════════════════════════════════════════════════════════

export function EndpointsPanel() {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const { formatDate } = useOrgSettings()
  const endpointsQ = useQuery({
    queryKey: ['integrations', 'device-endpoints'],
    queryFn:  () => api.integrations.deviceEndpoints.list(),
  })
  const endpoints = useMemo(() => Array.isArray(endpointsQ.data) ? endpointsQ.data : [], [endpointsQ.data])

  const [creating, setCreating] = useState(false)
  const [justIssued, setJustIssued] = useState<DeviceWebhookEndpointIssued | null>(null)

  const revoke = useMutation({
    mutationFn: (id: string) => api.integrations.deviceEndpoints.revoke(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations', 'device-endpoints'] })
      toast.success('Endpoint revoked')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to revoke'),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.integrations.deviceEndpoints.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations', 'device-endpoints'] })
      toast.success('Endpoint deleted')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  return (
    <Card>
      <SectionHeader
        icon={<DevicesOtherOutlinedIcon sx={{ fontSize: 20 }} />}
        title="MPulse webhook endpoints"
        subtitle="Create a URL for each MPulse device or model group, then paste it into that device's platform — any device with the URL can post feedback events, no header/API key needed."
        action={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setCreating(true)}
          >
            Create endpoint
          </Button>
        }
      />
      <CardContent sx={{ pt: 0, px: 3.5, pb: 3.5 }}>
        {endpointsQ.isLoading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : endpoints.length === 0 ? (
          <Box sx={{
            py: 5, textAlign: 'center', border: '1px dashed',
            borderColor: 'divider', borderRadius: 2, color: 'text.secondary',
          }}>
            <Typography sx={{ fontSize: 13 }}>
              No endpoints yet. Create one to get a URL for your MPulse devices to post to.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Token</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Last hit</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {endpoints.map((ep) => {
                const revoked = !!ep.revoked_at
                return (
                  <TableRow key={ep.id} hover>
                    <TableCell sx={{ fontSize: 13 }}>{ep.name}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, fontFamily: 'monospace', color: 'text.secondary' }}>
                      {ep.token_prefix}…
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                      {ep.last_hit_at ? formatDate(ep.last_hit_at, true) : 'Never'}
                    </TableCell>
                    <TableCell>
                      {revoked
                        ? <Chip size="small" label="Revoked" color="default" sx={{ fontSize: 11, height: 20 }} />
                        : <Chip size="small" label="Active"  color="success" sx={{ fontSize: 11, height: 20 }} />}
                    </TableCell>
                    <TableCell align="right">
                      {!revoked && (
                        <Tooltip title="Revoke">
                          <span>
                            <IconButton
                              size="small"
                              disabled={revoke.isPending}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: 'Revoke this endpoint?',
                                  message: 'Devices posting to this URL will start getting 401s immediately. This cannot be undone.',
                                  confirmLabel: 'Revoke',
                                  variant: 'danger',
                                })
                                if (ok) revoke.mutate(ep.id)
                              }}
                            >
                              <BlockOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            size="small"
                            disabled={remove.isPending}
                            onClick={async () => {
                              const ok = await confirm({
                                title: 'Delete this endpoint row?',
                                message: 'Removes the record entirely. Revoke first if a device might still be using it.',
                                confirmLabel: 'Delete',
                                variant: 'danger',
                              })
                              if (ok) remove.mutate(ep.id)
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <CreateEndpointDialog
        open={creating}
        onClose={() => setCreating(false)}
        onIssued={(ep) => {
          setCreating(false)
          setJustIssued(ep)
          qc.invalidateQueries({ queryKey: ['integrations', 'device-endpoints'] })
        }}
      />

      <ShowUrlDialog endpoint={justIssued} onClose={() => setJustIssued(null)} />
    </Card>
  )
}

function CreateEndpointDialog({ open, onClose, onIssued }: {
  open: boolean
  onClose: () => void
  onIssued: (ep: DeviceWebhookEndpointIssued) => void
}) {
  const [name, setName] = useState('')

  useEffect(() => {
    if (open) setName('')
  }, [open])

  const issue = useMutation({
    mutationFn: () => api.integrations.deviceEndpoints.issue({ name: name.trim() }),
    onSuccess: (ep) => onIssued(ep),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to create endpoint'),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Create a webhook endpoint</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          size="small"
          label="Name"
          placeholder="e.g. MPulse Basic — Building A"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          autoFocus
          helperText="Just for your own reference — shows up in the list and in the event log."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">Cancel</Button>
        <Button
          onClick={() => issue.mutate()}
          disabled={!name.trim() || issue.isPending}
          variant="contained"
          size="small"
        >
          {issue.isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function ShowUrlDialog({ endpoint, onClose }: {
  endpoint: DeviceWebhookEndpointIssued | null
  onClose: () => void
}) {
  const copy = () => {
    if (!endpoint) return
    navigator.clipboard.writeText(endpoint.webhook_url)
      .then(() => toast.success('Copied'))
      .catch(() => toast.error('Copy failed — select and copy manually'))
  }

  return (
    <Dialog open={!!endpoint} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        Save this URL — it won't be shown again
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning" sx={{ '& .MuiAlert-message': { fontSize: 13 } }}>
            Paste this URL into the MPulse device's platform as its callback/feedback URL now.
            We only store a hash of the token in it; reopening this dialog will not reveal it
            again. If you lose it, create a new endpoint and revoke this one.
          </Alert>
          <TextField
            size="small"
            fullWidth
            value={endpoint?.webhook_url ?? ''}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: 12.5 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={copy}>
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onFocus={(e) => e.target.select()}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" size="small">I have saved it</Button>
      </DialogActions>
    </Dialog>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// PANEL B — Device webhook event log
// ═══════════════════════════════════════════════════════════════════════════

export function DeviceEventsPanel() {
  const { formatDate } = useOrgSettings()
  const eventsQ = useQuery({
    queryKey: ['integrations', 'device-events'],
    queryFn:  () => api.integrations.deviceEvents.log({ limit: 100 }),
  })
  const events = useMemo(() => Array.isArray(eventsQ.data) ? eventsQ.data : [], [eventsQ.data])
  const [expanded, setExpanded] = useState<string | null>(null)

  return (
    <Card>
      <SectionHeader
        icon={<DevicesOtherOutlinedIcon sx={{ fontSize: 20 }} />}
        title="Device events"
        subtitle="Every hit on any of this org's device webhooks — link a device to an asset from the asset's Telemetry tab."
        action={
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" disabled={eventsQ.isFetching} onClick={() => eventsQ.refetch()}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        }
      />
      <CardContent sx={{ pt: 0, px: 3.5, pb: 3.5 }}>
        {eventsQ.isLoading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : events.length === 0 ? (
          <Box sx={{
            py: 5, textAlign: 'center', border: '1px dashed',
            borderColor: 'divider', borderRadius: 2, color: 'text.secondary',
          }}>
            <Typography sx={{ fontSize: 13 }}>
              No device has hit a webhook yet.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Received</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>IMEI</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Asset</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Soft points updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((ev) => (
                <DeviceEventRow
                  key={ev.id}
                  event={ev}
                  open={expanded === ev.id}
                  onToggle={() => setExpanded((cur) => (cur === ev.id ? null : ev.id))}
                  formatDate={formatDate}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

function DeviceEventRow({ event, open, onToggle, formatDate }: {
  event: DeviceWebhookEvent
  open: boolean
  onToggle: () => void
  formatDate: (d: string, withTime?: boolean) => string
}) {
  const statusColor = event.status === 'matched' ? 'success' : event.status === 'error' ? 'error' : 'default'
  return (
    <>
      <TableRow hover onClick={onToggle} sx={{ cursor: 'pointer' }}>
        <TableCell sx={{ width: 32 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontSize: 12.5, color: 'text.secondary' }}>
          {formatDate(event.received_at, true)}
        </TableCell>
        <TableCell sx={{ fontSize: 12.5, fontFamily: 'monospace' }}>
          {event.imei ?? '—'}
        </TableCell>
        <TableCell sx={{ fontSize: 13 }}>
          {event.asset_name ?? <span style={{ color: 'inherit', opacity: 0.5 }}>Unmatched</span>}
        </TableCell>
        <TableCell>
          <Chip size="small" label={event.status} color={statusColor} sx={{ fontSize: 11, height: 20 }} />
        </TableCell>
        <TableCell sx={{ fontSize: 13 }}>{event.soft_points_updated}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
              {event.error_message && (
                <Alert severity="error" sx={{ mb: 1.5, '& .MuiAlert-message': { fontSize: 12.5 } }}>
                  {event.error_message}
                </Alert>
              )}
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                Raw payload
              </Typography>
              <Box component="pre" sx={{
                m: 0, p: 1.5, borderRadius: 1, bgcolor: 'background.paper',
                fontSize: 12, fontFamily: 'monospace', overflowX: 'auto',
                border: '1px solid', borderColor: 'divider',
              }}>
                {JSON.stringify(event.raw_payload, null, 2)}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

// ─── Small layout helpers ───────────────────────────────────────────────────

function SectionHeader({ icon, title, subtitle, action }: {
  icon: React.ReactNode
  title: string
  subtitle?: string
  action?: React.ReactNode
}) {
  return (
    <Box sx={{ px: 3.5, pt: 3, pb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: 2, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center',
        bgcolor: (t) => `${t.palette.primary.main}18`, color: 'primary.main',
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em' }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>
        )}
      </Box>
      {action}
    </Box>
  )
}
